// src/stores/currencyStore.js
import { create } from 'zustand';
import currencyService from '../services/currencyService';

const useCurrencyStore = create((set, get) => ({
  // State
  currencies: [],
  currencyOptions: [],
  baseCurrency: null,
  selectedCurrency: null,
  isLoading: false,
  error: null,

  // Fetch all currencies
  fetchCurrencies: async (forceRefresh = false) => {
    set({ isLoading: true, error: null });
    try {
      const currencies = await currencyService.getAllCurrencies(forceRefresh);
      const currencyArray = Array.isArray(currencies) ? currencies : [];
      const baseCurrency = currencyArray.find(c => c.isBaseCurrency) || null;
      set({
        currencies: currencyArray,
        baseCurrency,
        isLoading: false
      });
      return currencyArray;
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to fetch currencies',
        currencies: [] // Ensure currencies is always an array
      });
      console.log('error', error);
      throw error;
    }
  },

  // Fetch currency options for select fields
  fetchCurrencyOptions: async () => {
    try {
      const options = await currencyService.getCurrencyOptions();
      set({ currencyOptions: options });
      return options;
    } catch (error) {
      console.error('Error fetching currency options:', error);
      // Return fallback options
      return currencyService.getCurrencyOptions();
    }
  },

  // Get currency by code
  getCurrencyByCode: (code) => {
    const { currencies } = get();
    return Array.isArray(currencies) ? currencies.find(c => c.code === code) : null;
  },

  // Create new currency
  createCurrency: async (currencyData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await currencyService.createCurrency(currencyData);
      
      if (response.success) {
        await get().fetchCurrencies(true);
        set({ isLoading: false });
        return response.data;
      }
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to create currency'
      });
      console.log('error', error);
      throw error;
    }
  },

  // Update currency
  updateCurrency: async (id, updates) => {
    set({ isLoading: true, error: null });
    try {
      const response = await currencyService.updateCurrency(id, updates);
      
      if (response.success) {
        await get().fetchCurrencies(true);
        set({ isLoading: false });
        return response.data;
      }
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to update currency'
      });
      console.log('error', error);
      throw error;
    }
  },

  // Delete/deactivate currency
  deleteCurrency: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await currencyService.deleteCurrency(id);
      
      // Handle both response structures
      if (response && (response.success || response.success !== false)) {
        await get().fetchCurrencies(true);
        set({ isLoading: false });
      }
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to deactivate currency'
      });
      console.log('error', error);
      throw error;
    }
  },

  // Restore/activate currency
  restoreCurrency: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await currencyService.restoreCurrency(id);
      
      // Handle both response structures
      if (response && (response.success || response.success !== false)) {
        await get().fetchCurrencies(true);
        set({ isLoading: false });
      }
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to restore currency'
      });
      console.log('error', error);
      throw error;
    }
  },

  // Set base currency
  setBaseCurrency: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await currencyService.setBaseCurrency(id);
      
      if (response.success) {
        // Immediately update the local state with the response
        const { currencies } = get();
        console.log('Before update - currencies:', currencies);
        console.log('Setting base currency ID:', id);
        
        const updatedCurrencies = currencies.map(currency => {
          const isNewBase = currency._id === id;
          console.log(`Currency ${currency.code} (${currency._id}): isBaseCurrency = ${isNewBase}`);
          return {
            ...currency,
            isBaseCurrency: isNewBase
          };
        });
        
        // Find the new base currency from the updated list
        const newBaseCurrency = updatedCurrencies.find(c => c._id === id) || response.data;
        console.log('New base currency:', newBaseCurrency);
        
        set({
          currencies: updatedCurrencies,
          baseCurrency: newBaseCurrency,
          isLoading: false
        });
        
        console.log('After update - currencies:', get().currencies);
        
        return response.data;
      }
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to set base currency'
      });
      console.log('error', error);
      throw error;
    }
  },

  // Update exchange rates
  updateExchangeRates: async (rates) => {
    set({ isLoading: true, error: null });
    try {
      const response = await currencyService.updateExchangeRates(rates);
      
      if (response.success) {
        await get().fetchCurrencies(true);
      }
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to update exchange rates'
      });
      console.log('error', error);
      throw error;
    }
  },

  // Convert currency
  convertCurrency: async (amount, fromCode, toCode) => {
    try {
      return await currencyService.convertCurrency(amount, fromCode, toCode);
    } catch (error) {
      console.log('error', error);
      throw error;
    }
  },

  // Format currency
  formatCurrency: (amount, currencyCode, locale) => {
    return currencyService.formatCurrency(amount, currencyCode, locale);
  },

  // Set selected currency
  setSelectedCurrency: (currency) => {
    set({ selectedCurrency: currency });
  },

  // Clear error
  clearError: () => set({ error: null }),

  // Clear store
  clearStore: () => {
    set({
      currencies: [],
      currencyOptions: [],
      baseCurrency: null,
      selectedCurrency: null,
      isLoading: false,
      error: null
    });
  }
}));

export default useCurrencyStore;